import React, { useState, useMemo } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Card, 
  Space, 
  Modal, 
  Form, 
  Tag, 
  Typography, 
  Row, 
  Col,
  Alert,
  Spin,
  Tooltip,
  Popconfirm,
  message,
  Avatar,
  Statistic,
  DatePicker,
  Divider,
  Descriptions,
  Badge
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  FilterOutlined,
  DownloadOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  CloseOutlined,
  SaveOutlined,
  LoadingOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  UserAddOutlined,
  LockOutlined,
  UnlockOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGuard } from '../components/PermissionGuard';
import { AdminPermission, AppUser as AppUserType } from '../generated/graphql';
import { useTranslation } from '../hooks/useTranslation';
import { 
  useAppUsers, 
  useAppUsersPaginated,
  useCreateAppUser, 
  // useUpdateAppUser, // Not available in backend yet
  useDeleteAppUser,
  useGraphQLError 
} from '../hooks/useGraphQL';
import AddressManagementModal from '../components/AddressManagementModal';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

// Using AppUserType from generated GraphQL types, but extending for status
interface User extends Omit<AppUserType, '__typename'> {
  status: 'active' | 'inactive';
}

const AppUser: React.FC = () => {
  const { canCreateUsers, canUpdateUsers, canDeleteUsers } = usePermissions();
  const { t } = useTranslation();
  const { handleError } = useGraphQLError();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [addressManagementUser, setAddressManagementUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  
  // GraphQL hooks
  const paginationDto = {
    page: currentPage,
    limit: pageSize,
    search: searchTerm || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter
  };
  
  const { data: appUsersData, loading: usersLoading, error: usersError, refetch } = useAppUsersPaginated(paginationDto);
  const [createAppUserMutation, { loading: createLoading }] = useCreateAppUser();
  // const [updateAppUserMutation, { loading: updateLoading }] = useUpdateAppUser(); // Not available yet
  const [deleteAppUserMutation, { loading: deleteLoading }] = useDeleteAppUser();
  
  // Transform GraphQL data to include status
  const users = useMemo(() => {
    if (!appUsersData?.usersPaginated?.users) return [];
    return appUsersData.usersPaginated.users.map((user: any): User => ({
      ...user,
      status: (user as any).deleted_at ? 'inactive' : 'active'
    }));
  }, [appUsersData]);

  const paginationInfo = appUsersData?.usersPaginated?.pagination;
  
  const isSubmitting = createLoading || deleteLoading;

  // Reset to first page when search or filter changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
    }
  };

  const handleCreateUser = async (values: any) => {
    try {
      const createUserDto = {
        email: values.email,
        firstname: values.firstname,
        lastname: values.lastname,
        phone_number: values.phone_number,
        password: 'temp123', // TODO: Handle password properly
        tenant: 'default'
      };
      
      await createAppUserMutation({
        variables: { createUserDto }
      });
      
      setShowCreateModal(false);
      form.resetFields();
      message.success(t('appUser.userCreated'));
      refetch(); // Refresh the data
    } catch (error) {
      console.error('Error creating user:', error);
      message.error(handleError(error));
    }
  };

  const handleUpdateUser = async (id: string, values: any) => {
    try {
      // Mock update since backend doesn't have updateUser mutation yet
      // In a real implementation, this would call the backend
      message.info('Update functionality is simulated - backend update mutation not yet available');
      
      // Simulate a delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      message.success(t('appUser.userUpdated'));
      setEditingUser(null);
      form.resetFields();
      
      // In a real implementation, you would refetch the data here
      // refetch();
    } catch (error) {
      console.error('Error updating user:', error);
      message.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (id: string) => {
    try {
      await deleteAppUserMutation({
        variables: { id }
      });
      
      message.success(t('appUser.userDeleted'));
      refetch(); // Refresh the data
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error(handleError(error));
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: 'success', text: t('appUser.active'), icon: <CheckCircleOutlined /> },
      inactive: { color: 'warning', text: t('appUser.inactive'), icon: <ExclamationCircleOutlined /> }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status, icon: null };
    return (
      <Tag color={config.color as any} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const columns = [
    {
      title: t('appUser.userDetails'),
      key: 'user',
      render: (user: User) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size={40}
            style={{ backgroundColor: '#1890ff' }}
            icon={<UserOutlined />}
          />
          <div style={{ marginLeft: 12 }}>
            <div style={{ fontWeight: 500 }}>
              {user.firstname || 'N/A'} {user.lastname || 'N/A'}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ID: {user.id}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: t('appUser.contact'),
      key: 'contact',
      render: (user: User) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <MailOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
            <Text style={{ fontSize: '12px' }}>{user.email}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PhoneOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
            <Text style={{ fontSize: '12px' }}>{user.phone_number || 'N/A'}</Text>
          </div>
        </div>
      ),
    },
    {
      title: t('appUser.status'),
      key: 'status',
      render: (user: User) => getStatusTag(user.status),
    },
    {
      title: t('common.createdAt'),
      key: 'created',
      render: (user: User) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CalendarOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
          <Text style={{ fontSize: '12px' }}>
            {dayjs(user.created_at).format('YYYY-MM-DD')}
          </Text>
        </div>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (user: User) => (
        <Space>
          <PermissionGuard permission={AdminPermission.UpdateUsers}>
            <Tooltip title={t('common.edit')}>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingUser(user);
                  form.setFieldsValue({
                    ...user,
                    created_at: user.created_at ? dayjs(user.created_at) : null
                  });
                }}
              />
            </Tooltip>
          </PermissionGuard>
          <Tooltip title={t('common.view')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => setViewingUser(user)}
            />
          </Tooltip>
          <Tooltip title={t('addresses.viewAddresses')}>
            <Button
              type="text"
              icon={<EnvironmentOutlined />}
              onClick={() => setAddressManagementUser(user)}
            />
          </Tooltip>
          <PermissionGuard permission={AdminPermission.DeleteUsers}>
            <Popconfirm
              title={t('appUser.deleteConfirm')}
              onConfirm={() => handleDeleteUser(user.id)}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Tooltip title={t('common.delete')}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </PermissionGuard>
        </Space>
      ),
    },
  ];



  // For statistics, we need to get all users (not paginated)
  // This is a temporary solution - in a real app, you might want separate queries for stats
  const { data: allUsersData } = useAppUsers();
  const allUsers = useMemo(() => {
    if (!allUsersData?.users) return [];
    return allUsersData.users.map((user: any): User => ({
      ...user,
      status: (user as any).deleted_at ? 'inactive' : 'active'
    }));
  }, [allUsersData]);

  const activeUsers = allUsers.filter((u: User) => u.status === 'active').length;
  const inactiveUsers = allUsers.filter((u: User) => u.status === 'inactive').length;
  const recentUsers = allUsers.filter((u: User) => {
    const createdDate = new Date(u.created_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return createdDate > thirtyDaysAgo;
  }).length;

  // Show error state
  if (usersError) {
    return (
      <div>
        <Alert
          message={t('common.error')}
          description={handleError(usersError)}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>{t('appUser.title')}</Title>
          <Text type="secondary">{t('appUser.subtitle')}</Text>
        </Col>
        <Col>
          {/* <PermissionGuard permission={AdminPermission.CreateUsers}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setShowCreateModal(true);
                form.resetFields();
              }}
            >
              {t('appUser.addUser')}
            </Button>
          </PermissionGuard> */}
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('appUser.totalUsers')}
              value={paginationInfo?.total || 0}
              prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('appUser.activeUsers')}
              value={activeUsers}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('appUser.inactiveUsers')}
              value={inactiveUsers}
              prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('appUser.newUsers')}
              value={recentUsers}
              prefix={<UserAddOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder={t('appUser.searchUsers')}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              allowClear
            />
          </Col>
          
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder={t('appUser.status')}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('appUser.allStatus')}</Option>
              <Option value="active">{t('appUser.active')}</Option>
              <Option value="inactive">{t('appUser.inactive')}</Option>
            </Select>
          </Col>
          
          {/* Export button removed */}
        </Row>
      </Card>

      {/* Users Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={usersLoading}
          pagination={{
            current: paginationInfo?.page || 1,
            pageSize: paginationInfo?.limit || 10,
            total: paginationInfo?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <UserModal
        visible={showCreateModal || !!editingUser}
        user={editingUser}
        isSubmitting={isSubmitting}
        form={form}
        onCancel={() => {
          setShowCreateModal(false);
          setEditingUser(null);
          form.resetFields();
        }}
        onSubmit={(values) => {
          if (editingUser) {
            handleUpdateUser(editingUser.id, values);
          } else {
            handleCreateUser(values);
          }
        }}
      />

      {/* View User Modal */}
      <ViewUserModal
        visible={!!viewingUser}
        user={viewingUser}
        onCancel={() => setViewingUser(null)}
      />

      {/* Address Management Modal */}
      <AddressManagementModal
        visible={!!addressManagementUser}
        userId={addressManagementUser?.id || ''}
        userName={`${addressManagementUser?.firstname || ''} ${addressManagementUser?.lastname || ''}`.trim() || 'Unknown User'}
        addresses={addressManagementUser?.addresses || []}
        onCancel={() => setAddressManagementUser(null)}
        onAddressesChange={async () => {
          const result = await refetch();
          // Update the addressManagementUser with fresh data
          if (result.data?.appUsers && addressManagementUser) {
            const updatedUser = result.data.appUsers.find((user: any) => user.id === addressManagementUser.id);
            if (updatedUser) {
              setAddressManagementUser(updatedUser);
            }
          }
        }}
      />
    </div>
  );
};

interface UserModalProps {
  visible: boolean;
  user?: User | null;
  isSubmitting: boolean;
  form: any;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}

const UserModal: React.FC<UserModalProps> = ({ 
  visible, 
  user, 
  isSubmitting, 
  form, 
  onCancel, 
  onSubmit 
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {user ? t('appUser.editUser') : t('appUser.addUser')}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{
          status: 'active'
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('appUser.firstName')}
              name="firstname"
              rules={[{ required: true, message: t('appUser.pleaseInputFirstName') }]}
            >
              <Input placeholder={t('appUser.firstNamePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('appUser.lastName')}
              name="lastname"
              rules={[{ required: true, message: t('appUser.pleaseInputLastName') }]}
            >
              <Input placeholder={t('appUser.lastNamePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={t('appUser.email')}
          name="email"
          rules={[
            { required: true, message: t('appUser.pleaseInputEmail') },
            { type: 'email', message: t('appUser.pleaseInputValidEmail') }
          ]}
        >
          <Input placeholder={t('appUser.emailPlaceholder')} />
        </Form.Item>

        <Form.Item
          label={t('appUser.phoneNumber')}
          name="phone_number"
          rules={[{ required: true, message: t('appUser.pleaseInputPhone') }]}
        >
          <Input placeholder={t('appUser.phonePlaceholder')} />
        </Form.Item>

        <Form.Item
          label={t('appUser.status')}
          name="status"
        >
          <Select>
            <Option value="active">{t('appUser.active')}</Option>
            <Option value="inactive">{t('appUser.inactive')}</Option>
          </Select>
        </Form.Item>

        <div style={{ textAlign: 'right', marginTop: 24 }}>
          <Space>
            <Button onClick={onCancel} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isSubmitting}
              icon={<SaveOutlined />}
            >
              {user ? t('appUser.updateUser') : t('appUser.createUser')}
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

interface ViewUserModalProps {
  visible: boolean;
  user?: User | null;
  onCancel: () => void;
}

const ViewUserModal: React.FC<ViewUserModalProps> = ({ 
  visible, 
  user, 
  onCancel 
}) => {
  const { t } = useTranslation();

  if (!user) return null;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {t('appUser.userDetails')}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel}>
          {t('common.close')}
        </Button>
      ]}
      width={700}
      destroyOnClose
    >
      <div style={{ padding: '16px 0' }}>
        <Row gutter={[24, 16]}>
          <Col span={24}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Avatar 
                size={80}
                style={{ backgroundColor: '#1890ff' }}
                icon={<UserOutlined />}
              />
              <div style={{ marginTop: 16 }}>
                <Title level={3} style={{ margin: 0 }}>
                  {user.firstname || 'N/A'} {user.lastname || 'N/A'}
                </Title>
                <Text type="secondary">ID: {user.id}</Text>
              </div>
            </div>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[24, 16]}>
          <Col span={12}>
            <Descriptions title={t('appUser.personalInfo')} column={1} size="small">
              <Descriptions.Item label={t('appUser.firstName')}>
                {user.firstname || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label={t('appUser.lastName')}>
                {user.lastname || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label={t('appUser.email')}>
                {user.email}
              </Descriptions.Item>
              <Descriptions.Item label={t('appUser.phoneNumber')}>
                {user.phone_number || 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          
          <Col span={12}>
            <Descriptions title={t('appUser.accountInfo')} column={1} size="small">
              <Descriptions.Item label={t('appUser.status')}>
                <Badge 
                  status={user.status === 'active' ? 'success' : 'warning'} 
                  text={user.status === 'active' ? t('appUser.active') : t('appUser.inactive')} 
                />
              </Descriptions.Item>
              <Descriptions.Item label={t('appUser.languagePreference')}>
                {user.language_preference || 'zh-HK'}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.createdAt')}>
                {dayjs(user.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>

        {(user.ic_type || user.ic_number || user.birthdate || user.gender) && (
          <>
            <Divider />
            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Descriptions title={t('appUser.identityInfo')} column={2} size="small">
                  {user.ic_type && (
                    <Descriptions.Item label={t('appUser.icType')}>
                      {user.ic_type}
                    </Descriptions.Item>
                  )}
                  {user.ic_number && (
                    <Descriptions.Item label={t('appUser.icNumber')}>
                      {user.ic_number}
                    </Descriptions.Item>
                  )}
                  {user.birthdate && (
                    <Descriptions.Item label={t('appUser.birthdate')}>
                      {dayjs(user.birthdate).format('YYYY-MM-DD')}
                    </Descriptions.Item>
                  )}
                  {user.age && (
                    <Descriptions.Item label={t('appUser.age')}>
                      {user.age}
                    </Descriptions.Item>
                  )}
                  {user.gender && (
                    <Descriptions.Item label={t('appUser.gender')}>
                      {user.gender}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Col>
            </Row>
          </>
        )}

        {user.deleted_at && (
          <>
            <Divider />
            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Descriptions title={t('appUser.deletionInfo')} column={1} size="small">
                  <Descriptions.Item label={t('appUser.deletedAt')}>
                    {dayjs(user.deleted_at).format('YYYY-MM-DD HH:mm:ss')}
                  </Descriptions.Item>
                  {user.deleted_reason && (
                    <Descriptions.Item label={t('appUser.deletionReason')}>
                      {user.deleted_reason}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Col>
            </Row>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AppUser; 